import React, { useState } from 'react';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import ProjectReports from './ProjectReports';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;

const ProjectModeration = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);

  // Recherche, filtres et pagination faits par le serveur (base de données).
  // La réponse est { projects, total, counts } (avant : traitée comme un tableau → page en erreur).
  const { data: projectsPage, isLoading } = useQuery(
    ['admin-projects', debouncedSearch, statusFilter, categoryFilter, page],
    async () => {
      const response = await axios.get('/api/admin/projects', {
        params: { search: debouncedSearch, status: statusFilter || 'all', category: categoryFilter || 'all', page, limit: PAGE_SIZE },
      });
      return response.data;
    },
    { keepPreviousData: true }
  );
  const projects = projectsPage?.projects || [];
  const totalProjects = projectsPage?.total || 0;
  const counts = projectsPage?.counts || {};

  // Mutation pour approuver un projet
  const approveProjectMutation = useMutation(
    async (projectId) => {
      const response = await axios.post(`/api/admin/projects/${projectId}/approve`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        toast.success('Projet approuvé avec succès');
      },
      onError: (error) => {
        console.error('Erreur approbation projet:', error);
        toast.error('Erreur lors de l\'approbation');
      }
    }
  );

  // Mutation pour rejeter un projet
  const rejectProjectMutation = useMutation(
    async ({ projectId, reason }) => {
      const response = await axios.post(`/api/admin/projects/${projectId}/reject`, { reason });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        toast.success('Projet rejeté avec succès');
      },
      onError: (error) => {
        console.error('Erreur rejet projet:', error);
        toast.error('Erreur lors du rejet');
      }
    }
  );

  const filteredProjects = projects;

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setShowProjectModal(true);
  };

  const handleApproveProject = (projectId) => {
    if (window.confirm('Êtes-vous sûr de vouloir approuver ce projet ?')) {
      approveProjectMutation.mutate(projectId);
    }
  };

  const handleRejectProject = (projectId) => {
    const reason = window.prompt('Raison du rejet (optionnel):');
    if (reason !== null) {
      rejectProjectMutation.mutate({ projectId, reason });
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
      case 'approved':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4" />;
      case 'rejected':
        return <XCircleIcon className="h-4 w-4" />;
      case 'draft':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Modération des Projets</h1>
            <p className="text-gray-600 mt-1">Approuver, rejeter et gérer les projets soumis</p>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center">
              <ClockIcon className="h-4 w-4 mr-1 text-yellow-500" />
              <span>{counts.pending || 0} en attente</span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500" />
              <span>{counts.active || 0} approuvés</span>
            </div>
          </div>
        </div>
      </div>

      <ProjectReports />

      {/* Filtres et Recherche */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un projet..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="active">Approuvé</option>
            <option value="rejected">Rejeté</option>
            <option value="draft">Brouillon</option>
          </select>
          
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Toutes les catégories</option>
            <option value="Technologie">Technologie</option>
            <option value="Santé">Santé</option>
            <option value="Finance">Finance</option>
            <option value="Éducation">Éducation</option>
            <option value="E-commerce">E-commerce</option>
            <option value="Immobilier">Immobilier</option>
            <option value="Transport">Transport</option>
            <option value="Énergie">Énergie</option>
            <option value="Agriculture">Agriculture</option>
            <option value="Tourisme">Tourisme</option>
          </select>
          
          <div className="flex items-center text-sm text-gray-600">
            <FunnelIcon className="h-4 w-4 mr-1" />
            {totalProjects} projet(s) trouvé(s)
          </div>
        </div>
      </div>

      {/* Tableau des projets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Projet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auteur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Catégorie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Financement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Soumis le
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProjects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="text-sm font-medium text-text truncate">
                        {project.title}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {project.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{project.author_name || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {project.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(project.status)}`}>
                      {getStatusIcon(project.status)}
                      <span className="ml-1">{project.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {project.fundingNeeded.toLocaleString()}€
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(project.createdAt).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewProject(project)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Voir le projet"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {project.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApproveProject(project.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Approuver"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRejectProject(project.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Rejeter"
                          >
                            <XCircleIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalProjects > PAGE_SIZE && (
        <div className="flex items-center justify-end space-x-2 text-sm text-gray-600">
          <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40">Précédent</button>
          <span>Page {page} / {Math.max(1, Math.ceil(totalProjects / PAGE_SIZE))}</span>
          <button type="button" disabled={page * PAGE_SIZE >= totalProjects} onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40">Suivant</button>
        </div>
      )}

      {/* Modal de visualisation du projet */}
      {showProjectModal && selectedProject && (
        <ProjectViewModal
          project={selectedProject}
          onClose={() => setShowProjectModal(false)}
          onApprove={() => {
            handleApproveProject(selectedProject.id);
            setShowProjectModal(false);
          }}
          onReject={() => {
            handleRejectProject(selectedProject.id);
            setShowProjectModal(false);
          }}
        />
      )}
    </div>
  );
};

// Composant modal pour visualiser un projet
const ProjectViewModal = ({ project, onClose, onApprove, onReject }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-text">{project.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Contenu principal */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-text mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{project.description}</p>
              </div>

              {project.businessModel && (
                <div>
                  <h3 className="text-lg font-semibold text-text mb-2">Modèle d'Affaires</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{project.businessModel}</p>
                </div>
              )}

              {project.targetMarket && (
                <div>
                  <h3 className="text-lg font-semibold text-text mb-2">Marché Cible</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{project.targetMarket}</p>
                </div>
              )}

              {project.competitiveAdvantage && (
                <div>
                  <h3 className="text-lg font-semibold text-text mb-2">Avantage Concurrentiel</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{project.competitiveAdvantage}</p>
                </div>
              )}
            </div>

            {/* Informations secondaires */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-text mb-4">Informations du Projet</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Catégorie:</span>
                    <span className="font-medium">{project.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Stade:</span>
                    <span className="font-medium">{project.stage}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Financement:</span>
                    <span className="font-medium">{project.fundingNeeded.toLocaleString()}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pays:</span>
                    <span className="font-medium">{project.country}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ROI attendu:</span>
                    <span className="font-medium">{project.expectedROI}%</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-text mb-4">Auteur</h3>
                <div className="space-y-2">
                  <p className="font-medium">{project.author_name || '—'}</p>
                </div>
              </div>

              {project.status === 'pending' && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-text mb-4">Actions de Modération</h3>
                  <div className="space-y-3">
                    <button
                      onClick={onApprove}
                      className="w-full flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      Approuver le projet
                    </button>
                    <button
                      onClick={onReject}
                      className="w-full flex items-center justify-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <XCircleIcon className="h-5 w-5 mr-2" />
                      Rejeter le projet
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModeration;

